import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import GroupsIcon from '@mui/icons-material/Groups';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import config from '../config';
import RuleScreen from '../shared/RuleScreen';
import FilterBar from '../shared/FilterBar';
import StatTile from '../shared/StatTile';
import DataTable from '../shared/DataTable';
import Pagination, { normalizePagination } from '../shared/Pagination';
import DetailDrawer from '../shared/DetailDrawer';
import { buildParams } from '../shared/apiParams';

const API_URL = config.apiUrl;
const EMPTY = { blocks: [], gramPanchayats: [], villages: [], enumerators: [], startDate: '', endDate: '', flagFilter: 'all' };

const FLAG_OPTIONS = [
  { value: 'all', label: 'All Surveys' },
  { value: 'eligible', label: 'Eligible > Auto' },
  { value: 'beneficiary', label: 'Beneficiary > Eligible' },
  { value: 'both', label: 'Both Flags' },
];

const TABS = [
  { key: 'enumerator', label: 'By Enumerator', col: 'Enumerator ID' },
  { key: 'block', label: 'By Block', col: 'Block Name' },
  { key: 'gp', label: 'By Gram Panchayat', col: 'Gram Panchayat' },
  { key: 'village', label: 'By Village', col: 'Village Name' },
];

export default function Subhadra({ rule }) {
  const [activeTab, setActiveTab] = useState('enumerator');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(normalizePagination({}));
  const [applied, setApplied] = useState(EMPTY);
  const [drawerRow, setDrawerRow] = useState(null);

  const tab = TABS.find((t) => t.key === activeTab);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams(applied, {
        map: { enumerators: 'enumerator_id' },
        extra: { page, limit: pageSize },
      });
      if (activeTab !== 'enumerator') params.append('forceGroupBy', activeTab);
      const res = await axios.get(`${API_URL}/subhadra?${params}`);
      if (res.data?.success) {
        const data = res.data.data || {};
        setRows(data.enumerators || []);
        setPagination(normalizePagination(data.pagination || {}));
      }
    } catch (err) {
      console.error('Error fetching Subhadra data:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, applied, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Page-scoped flag summary for the tiles.
  const pageSurveys = rows.reduce((s, r) => s + (Number(r.totalSurveys) || 0), 0);
  const pageEligibleFlags = rows.reduce((s, r) => s + (Number(r.flagCounts?.eligibleGreater) || 0), 0);
  const pageBeneficiaryFlags = rows.reduce((s, r) => s + (Number(r.flagCounts?.beneficiaryGreater) || 0), 0);

  const tiles = (
    <>
      <StatTile label={tab.label.replace('By ', '')} value={pagination.total} icon={GroupsIcon} accent="var(--cat-subhadra)" />
      <StatTile label="Surveys" value={pageSurveys} icon={GroupsIcon} accent="var(--primary)" sub="Current page" />
      <StatTile label="Eligible > Auto" value={pageEligibleFlags} icon={WarningAmberIcon} accent="var(--warning)" sub="Current page" />
      <StatTile label="Beneficiary > Eligible" value={pageBeneficiaryFlags} icon={WarningAmberIcon} accent="var(--danger)" sub="Current page" />
    </>
  );

  const flagBadge = (count, pct, high) => (
    <span className={`badge ${high ? 'badge-warning' : 'badge-neutral'}`}>
      {count || 0} ({pct || '0.00'}%)
    </span>
  );

  const columns = [
    { key: 'enumerator_id', label: tab.col, render: (r) => <span className="cell-strong">{r.enumerator_id || 'N/A'}</span> },
    { key: 'totalSurveys', label: 'Total Surveys', align: 'center', render: (r) => <span className="cell-strong">{r.totalSurveys}</span> },
    { key: 'eligibleAutoTotal', label: 'Eligible Auto', align: 'center', render: (r) => r.eligibleAutoTotal || 0 },
    { key: 'eligibleWomenTotal', label: 'Eligible Women', align: 'center', render: (r) => r.eligibleWomenTotal || 0 },
    { key: 'beneficiaryTotal', label: 'Beneficiary', align: 'center', render: (r) => r.beneficiaryTotal || 0 },
    {
      key: 'eligibleGreater', label: 'Eligible > Auto', align: 'center',
      render: (r) => flagBadge(r.flagCounts?.eligibleGreater, r.percentages?.eligibleGreater, parseFloat(r.percentages?.eligibleGreater || 0) > 10),
    },
    {
      key: 'beneficiaryGreater', label: 'Beneficiary > Eligible', align: 'center',
      render: (r) => flagBadge(r.flagCounts?.beneficiaryGreater, r.percentages?.beneficiaryGreater, parseFloat(r.percentages?.beneficiaryGreater || 0) > 10),
    },
  ];

  const filterBar = (
    <>
      <div className="tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`tab-btn${activeTab === t.key ? ' active' : ''}`}
            onClick={() => {
              if (t.key !== activeTab) {
                setActiveTab(t.key);
                setPage(1);
              }
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      <FilterBar
        onApply={(v) => { setApplied(v); setPage(1); }}
        onClear={() => { setApplied(EMPTY); setPage(1); }}
        loading={loading}
        extraFilters={[
          { name: 'flagFilter', label: 'Flag Type', options: FLAG_OPTIONS, defaultValue: 'all', isClearable: false },
        ]}
      />
    </>
  );

  return (
    <RuleScreen
      title={rule?.title || 'Subhadra Eligibility'}
      subtitle="Subhadra eligibility and beneficiary flag counts by enumerator, block, gram panchayat, and village."
      icon={VolunteerActivismIcon}
      backTo={rule ? `/category/subhadra` : undefined}
      backLabel="Back to Subhadra"
      tiles={tiles}
      filterBar={filterBar}
    >
      <div className="card">
        <h3 className="card-title">Statistics by {tab.label.replace('By ', '')}</h3>
        <Pagination
          pagination={pagination}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          unitLabel="groups"
          shown={rows.length}
        />
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          emptyMessage='No data found. Adjust filters and click "Apply Filters".'
          onRowClick={(r) => setDrawerRow(r)}
          getRowKey={(r, i) => `${r.enumerator_id}-${i}`}
        />
      </div>

      <DetailDrawer
        open={!!drawerRow}
        onClose={() => setDrawerRow(null)}
        title={drawerRow ? `${tab.col}: ${drawerRow.enumerator_id}` : ''}
        sections={
          drawerRow
            ? [
                {
                  title: 'Totals',
                  items: [
                    { label: 'Total Surveys', value: drawerRow.totalSurveys },
                    { label: 'Eligible Auto', value: drawerRow.eligibleAutoTotal || 0 },
                    { label: 'Eligible Women', value: drawerRow.eligibleWomenTotal || 0 },
                    { label: 'Beneficiary', value: drawerRow.beneficiaryTotal || 0 },
                  ],
                },
                {
                  title: 'Flags',
                  items: [
                    { label: 'Eligible > Auto', value: `${drawerRow.flagCounts?.eligibleGreater || 0} (${drawerRow.percentages?.eligibleGreater || '0.00'}%)` },
                    { label: 'Beneficiary > Eligible', value: `${drawerRow.flagCounts?.beneficiaryGreater || 0} (${drawerRow.percentages?.beneficiaryGreater || '0.00'}%)` },
                  ],
                },
              ]
            : []
        }
      />
    </RuleScreen>
  );
}
